import { useMemo } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import { COLLECTION_LAYER } from "@shared/types/collection";
import { collectionBlocksCollection, } from "@/data/collection-blocks-collection";
/**
 * For each Stack in `collections`, the max `sortKey` (mirrors a
 * block's `surfaceAt`) across its currently-loaded members. Drives
 * the Stream's content-anchored sort: a Stack's grid position
 * tracks the most recent member it contains, so closed stacks
 * naturally interleave with blocks at the date of their content
 * rather than always landing at "stack-was-organized-just-now"
 * on `lastAddedAt`.
 *
 * Reactive: a single `useLiveQuery` against
 * `collectionBlocksCollection` means moves / adds / removes inside
 * any open Stack re-emit the map and re-position the tile in the
 * same React commit as the underlying mutation.
 *
 * The map keys are stack ids; values are the ISO timestamp string.
 * Stacks with zero loaded members are absent from the map — callers
 * should fall through to the regular `lastAddedAt`/`createdAt`
 * activity timestamp in that case.
 *
 * Pagination note: stacks are eagerly loaded up to
 * `STACK_DETAIL_PAGE_SIZE = 100` members at parent-collection load
 * time (see `CollectionDetailTabContent`'s prefetch). Stacks beyond
 * that cap still surface the correct "newest member" because the
 * loader pages from `surfaceAt desc`, so the most recent block is
 * always in the loaded slice.
 */
export function useStackNewestMemberAt(collections) {
    const stackIdSet = useMemo(() => {
        const s = new Set();
        for (const c of collections ?? []) {
            if (c.layerIndex === COLLECTION_LAYER.stack)
                s.add(c.id);
        }
        return s;
    }, [collections]);
    const { data } = useLiveQuery((q) => q.from({ r: collectionBlocksCollection }), []);
    return useMemo(() => {
        const out = new Map();
        if (stackIdSet.size === 0)
            return out;
        for (const row of (data ?? [])) {
            if (stackIdSet.has(row.scopeId) === false)
                continue;
            const cur = out.get(row.scopeId);
            if (cur === undefined || row.sortKey.localeCompare(cur) > 0) {
                out.set(row.scopeId, row.sortKey);
            }
        }
        return out;
    }, [data, stackIdSet]);
}
